import { ApolloProvider as Provider } from '@apollo/client/react/compiled'
import { createApolloClient } from '@dolpheen/apollo/client'
import useLocale from '@hooks/useLocale'
import type { ReactNode } from 'react'
import { useEffect, useMemo } from 'react'

import type { ApolloHandlers } from './handlers'
import { getApolloHandlers, resetApolloHandler, setApolloHandler } from './handlers'

export function ApolloProvider({ children }: { children: ReactNode }) {
  const { locale } = useLocale()

  const apolloClient = useMemo(() => createApolloClient(locale, getApolloHandlers), [locale])
  return <Provider client={apolloClient}>{children}</Provider>
}

/**
 * Register a callback for one of the `ApolloHandlers` keys (e.g. `onAuthError`).
 * The callback is invoked by Apollo links when the corresponding event fires.
 * Reverts to the no-op default on unmount.
 */
export function useApolloHandler<K extends keyof ApolloHandlers>(
  key: K,
  handler: ApolloHandlers[K],
): void {
  useEffect(() => {
    setApolloHandler(key, handler)
    return () => {
      resetApolloHandler(key)
    }
  }, [key, handler])
}
